// File: ./src/js/modules/cdc-mappa-svg-normativa.js

/**
 * 
 * Inizializza il blocco Normativa Regionale con mappa interattiva e sidebar.
 * 
 * Gestisce il caricamento dei dati JSON, l'interazione con la mappa SVG
 * e l'elenco delle regioni, e l'aggiornamento dinamico della sidebar
 * con i dettagli della normativa regionale.
 * 
 * Struttura dati JSON attesa:
 * {
 *   "regione-slug": {
 *       "nome": "Nome Regione",
 *       "docs": [
 *        {
 *          "titolo": "Titolo Documento",
 *          "file": "URL Documento PDF",
 *          "data": "Data Documento",
 *          "peso": "Peso Documento"
 *        },
 *        ...
 *       ]  
 *    },
 *    ...
 * } 
 * 
 * File correlati:
 * - Template PHP: global-templates/normativa/blocco-normativa.php
 * - File SASS: _mappa.scss
 * 
 * @param {string} data - ID dello script contenente i dati JSON delle regioni
 * @returns {void}
 * 
 */

export default function initBloccoNormativaRegionale(data = "normativa-regioni-data") {

    const dataScript = document.getElementById(data);
    if (!dataScript) {
        console.warn(`❌ Script con ID "${data}" non trovato.`);
        return;
    }

    let regionData = {};

    // Parsing JSON
    try {
        regionData = JSON.parse(dataScript.textContent);
    } catch (e) {
        console.error("❌ JSON 'normativa-regioni' non valido", e);
        return;
    }

    // Validazione dati

    // if (regionData == null || typeof regionData !== "object") {
    //     console.warn("❌ Dati 'normativa-regioni' non validi.");
    //     return;
    // }

    if (Object.keys(regionData).length === 0) {
        console.warn("❌ Dati 'normativa-regioni' vuoti dopo il parsing.");
        return;
    }

    const sidebar = document.getElementById("normativaSidebar");
    if (!sidebar) {
        console.warn("❌ Sidebar 'normativaSidebar' non trovata.");
        return;
    }

    const defaultTitle = sidebar.dataset.defaultTitle || "";
    const defaultDesc = sidebar.dataset.defaultDesc || "";
    const emptyMessage = sidebar.dataset.emptyMessage || "";

    const viewMap = document.querySelector('[data-view="map"]');
    const viewList = document.querySelector('[data-view="list"]');
    const btnShowList = document.querySelector('[data-action="show-list"]');
    const btnShowMap = document.querySelector('[data-action="show-map"]');

    const mapRegions = document.querySelectorAll(
        "#mappaRegioniItalia [data-reg]"
    );
    const listButtons = document.querySelectorAll(
        ".normativa-regioni__lista [data-reg]"
    );

    // Helper → cambia vista mappa/elenco
    function setView(view) {
        if (!viewMap || !viewList || !btnShowList || !btnShowMap) return;

        const showMap = view === "map";

        viewMap.classList.toggle("is-active", showMap);
        viewList.classList.toggle("is-active", !showMap);
        viewList.hidden = showMap;
        viewMap.hidden = !showMap;

        btnShowList.classList.toggle("d-none", !showMap);
        btnShowMap.classList.toggle("d-none", showMap);
    }

    // Helper → reset sidebar allo stato iniziale
    function resetSidebar() {
        sidebar.innerHTML = "";

        const h2 = document.createElement("h2");
        h2.className =
            "h2 typo-type fw-title color-accent-2 mb-large normativa-sidebar__title";
        h2.textContent = defaultTitle;
        sidebar.appendChild(h2);

        const p = document.createElement("p");
        p.className =
            "text-paragraph-lg color-on-surface mb-large normativa-sidebar__desc";
        p.textContent = defaultDesc;
        sidebar.appendChild(p);
    }

    // Helper → pulizia stato active
    function clearActiveRegions() {
        mapRegions.forEach((el) => el.classList.remove("active"));
        listButtons.forEach((btn) => {
            btn.classList.remove("active");
            btn.setAttribute("aria-pressed", "false");
        });
    }

    // Render → dettaglio regione
    function openRegion(slug) {
        const data = regionData[slug];
        if (!data) {
            resetSidebar();
            return;
        }

        clearActiveRegions();

        // attivo regione su mappa
        mapRegions.forEach((el) => {
            if (el.dataset.reg === slug) {
                el.classList.add("active");
            }
        });

        // attivo regione nei pulsanti elenco
        listButtons.forEach((btn) => {
            if (btn.dataset.reg === slug) {
                btn.classList.add("active");
                btn.setAttribute("aria-pressed", "true");
            }
        });

        // sidebar dinamica
        sidebar.innerHTML = "";

        // pulsante chiudi
        const btnClose = document.createElement("button");
        btnClose.type = "button";
        btnClose.className = "normativa-sidebar__close";
        btnClose.setAttribute("aria-label", "Chiudi dettaglio regione");
        btnClose.textContent = "×";
        btnClose.addEventListener("click", function () {
            clearActiveRegions();
            resetSidebar();
        });
        sidebar.appendChild(btnClose);

        const h2 = document.createElement("h2");
        h2.className =
            "h2 typo-type fw-title color-accent-2 mb-large normativa-sidebar__title";
        h2.textContent = data.nome || slug;
        sidebar.appendChild(h2);

        const docs = Array.isArray(data.docs) ? data.docs : [];

        if (!docs.length) {
            const p = document.createElement("p");
            p.className = "text-paragraph-lg color-on-surface mb-large";
            p.textContent =
                emptyMessage || "Nessuna normativa presente per questa regione.";
            sidebar.appendChild(p);
            return;
        }

        const list = document.createElement("div");
        list.className = "normativa-sidebar__docs";
        sidebar.appendChild(list);

        // elenco documenti PDF - Normativa Regionale
        docs.forEach((doc) => {
            const item = document.createElement("article");
            item.className = "normativa-doc";

            // LINK: titolo + icona
            const headerLink = document.createElement("a");
            headerLink.href = doc.file;
            headerLink.target = "_blank";
            headerLink.rel = "noopener noreferrer";
            headerLink.className = "normativa-doc__link";
            headerLink.setAttribute("aria-label", `Apri il documento: ${doc.titolo}`);

            item.appendChild(headerLink);

            const title = document.createElement("h3");
            title.className = "normativa-doc__title";
            title.textContent = doc.titolo || "";
            headerLink.appendChild(title);

            const icon = document.createElement("span");
            icon.className = "normativa-doc__icon";
            icon.setAttribute("aria-hidden", "true");
            headerLink.appendChild(icon);

            /* META */
            const meta = document.createElement("div");
            meta.className = "normativa-doc__meta";

            if (doc.data) {
                const spanData = document.createElement("span");
                spanData.className = "normativa-doc__date";
                spanData.textContent = doc.data;
                meta.appendChild(spanData);
            }

            if (doc.peso) {
                const spanPeso = document.createElement("span");
                spanPeso.className = "normativa-doc__size";
                spanPeso.textContent = doc.peso;
                meta.appendChild(spanPeso);
            }

            item.appendChild(meta);
            list.appendChild(item);
        });
    }

    // Bind → eventi vista mappa/elenco
    if (btnShowList && btnShowMap) {
        btnShowList.addEventListener("click", () => setView("list"));
        btnShowMap.addEventListener("click", () => setView("map"));
    }

    // Bind → eventi su mappa
    mapRegions.forEach((el) => {
        const slug = el.dataset.reg;
        if (!slug) return;

        el.setAttribute("role", "button");
        el.setAttribute("tabindex", "0");

        el.addEventListener("click", () => openRegion(slug));

        el.addEventListener("keydown", (evt) => {
            if (evt.key === "Enter" || evt.key === " ") {
                evt.preventDefault();
                openRegion(slug);
            }
        });
    });

    // Bind → eventi su pulsanti elenco
    listButtons.forEach((btn) => {
        const slug = btn.dataset.reg;
        if (!slug) return;

        btn.setAttribute("type", "button");
        btn.setAttribute("aria-pressed", "false");

        btn.addEventListener("click", () => openRegion(slug));
    });
}